<template>
  <wrapper
    class="assign-groups"
    :focus="value && value.length > 0"
    :disabled="disabled"
  >
    <div v-show="value && value.length > 0">
      <p class="inline-block">Assigned group(s)</p>

      <div
        class="flex items-center pt-3"
        v-for="(g, index) in value"
        :key="'parent-group' + index + '-' + componentKey"
      >
        <div class="flex-1 parent-group">
          {{ g.groupName ? g.groupName : g.name }}
          <grafokett-button
            style="float: right"
            class="small"
            @click="removeParentGroup(g)"
            >Remove</grafokett-button
          >
        </div>

        <div
          class="w-48"
          style="margin-left: 10px"
          v-if="shouldHaveDefaultPrinters && subscriptionType !== 'Free'"
        >
          <drop-down
            v-if="getAllPrintersForGroup(g.id).length > 0"
            placeholder="Select default printer"
            value-property="name"
            search-prop="name"
            :value="getDefaultPrinterForGroup(g.id)"
            :options="getAllPrintersForGroup(g.id)"
            @change="addDefaultPrinter(g.id, $event)"
            @input="setPrinter(g.id, $event)"
          />
        </div>

        <checkbox
          v-if="useCheckbox"
          class="mt-4 ml-5 mr-20"
          v-model="g.checked"
        />
      </div>
    </div>
    <div v-show="!groupsLockedIn">
      <p class="pt-3">
        <slot name="assignedParagraph">
          {{ text }}
        </slot>
      </p>
      <filter-and-sort-box class="pt-2" :data="groups" :headers="headersGroup">
        <template v-slot:d="{ d }">
          <tree-item
            :data="d"
            v-slot:default="slotProps"
            :classMethod="groupClass"
          >
            <span>{{ slotProps.group.groupName }}</span>
            <div style="float: right">
              <grafokett-button
                :test="slotProps"
                class="small"
                @click.stop="addParentGroup(slotProps.group)"
                >{{
                  doseGroupeExist(slotProps.group, value) ? "Added" : "Add"
                }}</grafokett-button
              >
            </div>
          </tree-item>
        </template>
      </filter-and-sort-box>
    </div>
  </wrapper>
</template>

<script>
import FilterAndSortBox from "../FilterAndSortBox";
import TreeItem from "../TreeItem";
import DropDown from "../forms/DropDown";
import GrafokettButton from "../forms/Button";
import Wrapper from "../Wrapper";
import Checkbox from "../forms/Checkbox";
import Vue from "vue";

export default {
  components: {
    GrafokettButton,
    FilterAndSortBox,
    TreeItem,
    DropDown,
    Wrapper,
    Checkbox
  },
  props: {
    groups: {
      type: Array,
      required: true
    },
    presetGroups: {
      type: Array,
      required: false
    },
    value: {
      type: Array
    },
    disabled: {
      type: Boolean,
      default: false
    },
    useCheckbox: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: "Assign group"
    },
    label: {
      type: Object,
      required: false
    },
    subscriptionType:{
      type: String,
      required: false
    }
  },
  computed: {
    shouldHaveDefaultPrinters() {
      return this.$route.name === "label";
    }
  },
  appClasses() {
    return this.shouldHaveDefaultPrinters ? "" : "has-default-printers";
  },
  data() {
    return {
      componentKey: 0,
      printerSelected: {},
      printerModels: [],
      parentGroup: [],
      groupsLockedIn: false,
      headersGroup: {
        groupName: "Group name"
      }
    };
  },
  watch: {
    value: {
      async handler() {
        if (this.shouldHaveDefaultPrinters && this.subscriptionType !== 'Free') {
          this.value.forEach(async group => {
            if (!this.printerModels[group.id]) {
              Vue.set(
                this.printerModels,
                group.id,
                await this.loadAllPrintersForGroupAsync(group.id)
              );
              Vue.set(
                this.printerSelected,
                group.id,
                await this.loadDefaultPrinterForGroupAsync(group.id)
              );
            }
          });
        }

        if (this.value.length !== this.parentGroup.length) {
          this.parentGroup = this.value.map(group => ({
            ...group,
            ...this.getGroupById(group.id, this.groups)
          }));
          this.$emit("input", this.parentGroup);
        }
      },
      immediate: true
    }
  },
  async mounted() {
    if (this.presetGroups) {
      const childGroups = this.groups.flatMap(g => g.children);
      const presetGroupIds = this.presetGroups.map(g => g.id);
      var assignGroups = this.groups
        .concat(childGroups)
        .filter(g => presetGroupIds.includes(g.id));
      assignGroups.forEach(ag => {
        this.addParentGroup(ag);
      });
    }
    this.componentKey++;
  },
  methods: {
    async loadAllPrintersForGroupAsync(groupId) {
      const response = await this.$store.$api.get(
        `/api/groups/${groupId}/printers`
      );
      return response.data.data;
    },
    async loadDefaultPrinterForGroupAsync(groupId) {
      const response = await this.$store.$api.get(
        `/api/labels/${this.label.labelId}/printers/${groupId}`
      );
      const printer = response.data.data;

      if (printer) {
        this.addDefaultPrinter(groupId, printer);
      }

      return printer;
    },
    getDefaultPrinterForGroup(groupId) {
      return this.printerSelected[groupId] ? this.printerSelected[groupId] : {};
    },
    getAllPrintersForGroup(groupId) {
      return this.printerModels[groupId] ? this.printerModels[groupId] : [];
    },
    setPrinter(groupId, printer) {
      this.printerSelected[groupId] = printer;
      this.componentKey++;
    },
    getGroupById(id, children) {
      if (!children) return undefined;
      let exists = children.find(group => group.id === id);
      if (exists) return exists;
      for (let i = 0; i < children.length; i++) {
        exists = this.getGroupById(id, children[i].children);
        if (exists) return exists;
      }
    },
    addDefaultPrinter(groupId, printer) {
      const group = this.getGroupById(groupId, this.groups);
      this.$emit("printerAdded", {
        group: group,
        printer: printer
      });
    },
    removeDefaultPrinter(groupId, printer) {
      Vue.set(this.printerSelected, groupId, null);

      const group = this.getGroupById(groupId, this.groups);
      this.$emit("printerRemoved", {
        group: group,
        printer: printer
      });
    },
    addParentGroup(group) {
      if (!group["checked"]) this.$set(group, "checked", false);
      if (this.doseGroupeExist(group, this.value)) return;
      const i = this.value.indexOf(group);
      if (i === -1 && !this.doseGroupeExist(group, this.value)) {
        this.parentGroup = [...this.value];
        this.parentGroup.push(group);
        this.removeChildGroups(group);
        this.$emit("input", this.parentGroup);
        this.$emit("groupAdded", group);
      }
    },
    removeChildGroups(group) {
      if (group.children) {
        for (let i = 0; i < group.children.length; i++) {
          this.removeChildGroups(group.children[i]);
          this.removeParentGroup(group.children[i]);
        }
      }
    },
    removeParentGroup(group) {
      const i = this.parentGroup.indexOf(group);
      if (i !== -1) {
        this.parentGroup.splice(i, 1);
        this.groupsLockedIn = false;
        this.$emit("input", this.parentGroup);
        this.$emit("removeGroup", group);

        if (this.printerSelected[group.id]) {
          const printer = this.printerSelected[group.id];
          this.removeDefaultPrinter(group.id, printer);
        }
      }
    },
    groupClass(group) {
      return { disabled: this.doseGroupeExist(group, this.value) };
    },
    doseGroupeExist(group, children) {
      if (!children) return false;
      let exists = children.includes(group);
      if (exists) return true;
      for (let i = 0; i < children.length; i++) {
        if (this.doseGroupeExist(group, children[i].children)) return true;
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
.assign-groups {
  @apply p-5 rounded;
}

.parent-group {
  background-color: theme("colors.textcolor");
  color: theme("colors.highlight");
  @apply p-4 font-semibold;
}

.parent-group:nth-child(1) {
  @apply rounded-t;
}

.parent-group:last-child {
  @apply rounded-b;
}

.parent-group .button {
  color: theme("colors.primary");
  @apply font-semibold;
}
</style>
